<template>
  <article id="print" class="medical-record-sheet">
    <div class="medical-record-sheet__alphabet" aria-hidden="true">
      <span v-for="letter in alphabet" :key="letter">{{ letter }}</span>
    </div>

    <header class="medical-record-sheet__header">
      <div>
        <span>Blanko Rekam Medis</span>
        <h1>{{ clinicName }}</h1>
      </div>
      <div class="medical-record-sheet__number">
        <span>Nomor rekam medis</span>
        <strong>{{ value(data.patient_id) }}</strong>
      </div>
    </header>

    <section class="medical-record-sheet__patient">
      <div class="medical-record-sheet__identity">
        <div class="medical-record-sheet__avatar">{{ initials }}</div>
        <div>
          <span>Nama pasien</span>
          <strong>{{ value(data.patient_name) }}</strong>
          <small>{{ birthSummary }}</small>
        </div>
      </div>
      <div class="medical-record-sheet__facts">
        <div><span>Jenis kelamin</span><strong>{{ genderLabel }}</strong></div>
        <div><span>Orang tua/wali</span><strong>{{ value(data.parent_name) }}</strong></div>
        <div><span>Telepon</span><strong>{{ value(data.mobile_phone || data.phone) }}</strong></div>
        <div><span>Alergi</span><strong>{{ value(data.allergy, 'Tidak ada data') }}</strong></div>
        <div class="medical-record-sheet__fact-wide"><span>Alamat</span><strong>{{ value(data.address) }}</strong></div>
        <div class="medical-record-sheet__fact-wide"><span>Catatan</span><strong>{{ value(data.notes, 'Tidak ada catatan') }}</strong></div>
      </div>
    </section>

    <table class="medical-record-sheet__table">
      <thead>
        <tr>
          <th class="medical-record-sheet__date-column">Tanggal/Jam</th>
          <th>Uraian (S / O / A / P)</th>
          <th class="medical-record-sheet__instruction-column">Instruksi</th>
          <th class="medical-record-sheet__doctor-column">Dokter</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="index in 6" :key="index">
          <td></td><td></td><td></td><td></td>
        </tr>
      </tbody>
    </table>

    <footer class="medical-record-sheet__footer">
      <span>Dokumen rekam medis pasien</span>
      <span>Halaman 1</span>
    </footer>
  </article>
</template>

<script>
export default {
  props: {
    data: { type: Object, default: () => ({}) },
    items: { type: [Object, Array], default: () => [] },
  },
  computed: {
    alphabet() {
      return Array.from({ length: 26 }, (_, index) => String.fromCharCode(65 + index));
    },
    clinicName() {
      return window.configuration && window.configuration.clinic_name
        ? window.configuration.clinic_name
        : "Medhop";
    },
    initials() {
      return String(this.data.patient_name || "P")
        .split(" ").filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join("");
    },
    genderLabel() {
      const gender = String(this.data.gender || "").toLowerCase();
      if (["male", "l", "laki-laki", "laki laki"].includes(gender)) return "Laki-laki";
      if (["female", "p", "perempuan"].includes(gender)) return "Perempuan";
      return this.value(this.data.gender);
    },
    birthSummary() {
      const age = this.data.age !== null && this.data.age !== undefined ? `${this.data.age} tahun` : "Umur belum tersedia";
      return `${age} · ${this.value(this.data.display_birt_date || this.data.birt_date, 'Tanggal lahir belum tersedia')}`;
    },
  },
  methods: {
    value(value, fallback = "-") {
      return value === null || value === undefined || value === "" ? fallback : value;
    },
  },
};
</script>

<style lang="scss" scoped>
.medical-record-sheet {
  width: 790px; min-height: 1115px; padding: 28px; border: 1px solid #d6dde5; background: #fff;
  box-shadow: 0 14px 38px rgba(var(--skin-deep-rgb),.12); color: #172033; font-family: Arial, sans-serif;
}
.medical-record-sheet__alphabet { display: grid; grid-template-columns: repeat(26, 1fr); border: 1px solid #223044; }
.medical-record-sheet__alphabet span { padding: 4px 0; border-right: 1px solid #223044; font-size: 8px; font-weight: 800; text-align: center; }
.medical-record-sheet__alphabet span:last-child { border-right: 0; }
.medical-record-sheet__header { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 20px 2px 16px; border-bottom: 2px solid var(--skin-secondary); }
.medical-record-sheet__header span, .medical-record-sheet__number span { display: block; margin-bottom: 3px; color: #64748b; font-size: 9px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.medical-record-sheet__header h1 { margin: 0; color: var(--skin-deep); font-size: 20px; font-weight: 800; text-transform: uppercase; }
.medical-record-sheet__number { padding: 9px 12px; border: 1px solid #cfd9e3; border-radius: 7px; text-align: right; }
.medical-record-sheet__number strong { font-size: 13px; }
.medical-record-sheet__patient { display: grid; grid-template-columns: .85fr 1.15fr; gap: 20px; padding: 17px 0; }
.medical-record-sheet__identity { display: flex; align-items: center; gap: 11px; }
.medical-record-sheet__avatar { width: 44px; height: 44px; flex: 0 0 44px; display: grid; place-items: center; border-radius: 10px; background: #e8f5f4; color: #157b76; font-size: 14px; font-weight: 800; }
.medical-record-sheet__identity span, .medical-record-sheet__identity strong, .medical-record-sheet__identity small, .medical-record-sheet__facts span, .medical-record-sheet__facts strong { display: block; }
.medical-record-sheet__identity span, .medical-record-sheet__facts span { margin-bottom: 2px; color: #6f7e8f; font-size: 8px; font-weight: 700; text-transform: uppercase; }
.medical-record-sheet__identity strong { font-size: 13px; }
.medical-record-sheet__identity small { margin-top: 4px; color: #64748b; font-size: 9px; }
.medical-record-sheet__facts { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px 15px; }
.medical-record-sheet__facts strong { color: #253448; font-size: 9px; line-height: 1.4; }
.medical-record-sheet__fact-wide { grid-column: 1 / -1; }
.medical-record-sheet__table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 9px; }
.medical-record-sheet__table th, .medical-record-sheet__table td { border: 1px solid #334155; }
.medical-record-sheet__table th { padding: 9px 6px; background: #edf4f6; color: #243448; font-size: 9px; font-weight: 800; text-align: center; text-transform: uppercase; }
.medical-record-sheet__table tbody tr { height: 118px; }
.medical-record-sheet__date-column { width: 15%; }
.medical-record-sheet__instruction-column, .medical-record-sheet__doctor-column { width: 19%; }
.medical-record-sheet__footer { display: flex; justify-content: space-between; margin-top: 9px; color: #8491a0; font-size: 8px; }
@media print {
  .medical-record-sheet { width: 100%; min-height: auto; padding: 0; border: 0; box-shadow: none; }
}
</style>
